/**
 * Extracts latitude and longitude coordinates from a Google Maps URL.
 * 
 * Supports multiple Google Maps URL formats:
 * - https://www.google.com/maps/place/.../@LAT,LNG
 * - https://maps.google.com/?q=LAT,LNG
 * - https://www.google.com/maps/search/?api=1&query=LAT,LNG
 * - Embed URLs with center parameter
 * 
 * Note: Short links (maps.app.goo.gl) cannot be resolved client-side.
 * 
 * @param url - Google Maps URL string
 * @returns Object with lat and lng, or null if coordinates cannot be extracted
 */
export function extractCoordinatesFromUrl(url: string | null): { lat: number; lng: number } | null {
  if (!url) return null;

  let lat: number | null = null;
  let lng: number | null = null;

  try {
    // Pattern A: Standard /@lat,lng (most common)
    const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
      lat = parseFloat(atMatch[1]);
      lng = parseFloat(atMatch[2]);
    }

    // Pattern B: !3dlat!4dlng (very common for shared Place URLs)
    if (!lat || !lng) {
      const dataMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (dataMatch) {
        lat = parseFloat(dataMatch[1]);
        lng = parseFloat(dataMatch[2]);
      }
    }

    // Pattern C: Query param ?q=lat,lng
    if (!lat || !lng) {
      const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (qMatch) {
        lat = parseFloat(qMatch[1]);
        lng = parseFloat(qMatch[2]);
      }
    }

    // Pattern D: Search center ll=lat,lng
    if (!lat || !lng) {
      const llMatch = url.match(/[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (llMatch) {
        lat = parseFloat(llMatch[1]);
        lng = parseFloat(llMatch[2]);
      }
    }

    // Validate coordinates
    if (lat != null && lng != null && 
        !isNaN(lat) && !isNaN(lng) && 
        lat >= -90 && lat <= 90 && 
        lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  } catch (error) {
    console.error('[MapsUtils] Error extracting coordinates:', error);
  }

  return null;
}

/**
 * Checks if a Google Maps URL is a short link that cannot be resolved client-side.
 * @param url - Google Maps URL string
 * @returns true if the URL is a short link (maps.app.goo.gl, goo.gl/maps, etc.)
 */
export function isShortMapsUrl(url: string | null): boolean {
  if (!url) return false;
  return /(maps\.app\.goo\.gl|goo\.gl\/maps|bit\.ly)/i.test(url);
}

/**
 * Calculate the distance between two points on Earth using the Haversine formula.
 * Returns distance in kilometers.
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in kilometers
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Optimizes route using Nearest Neighbor algorithm.
 * Sorts points to minimize total travel distance starting from a given start point.
 * @param startPoint - Starting point [lat, lng]
 * @param points - Array of points with lat and lng properties
 * @returns Array of sorted points with their original data
 */
export function optimizeRoute<T extends { lat: number | null; lng: number | null }>(
  startPoint: [number, number],
  points: T[]
): T[] {
  if (points.length === 0) return [];
  
  const validPoints = points.filter(p => p.lat != null && p.lng != null) as Array<T & { lat: number; lng: number }>;
  if (validPoints.length === 0) return [];
  
  if (validPoints.length === 1) return validPoints;
  
  const sorted: T[] = [];
  const remaining = [...validPoints];
  let currentPoint: [number, number] = startPoint;
  
  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = haversineDistance(
      currentPoint[0],
      currentPoint[1],
      remaining[0].lat,
      remaining[0].lng
    );
    
    // Find the nearest point to current position
    for (let i = 1; i < remaining.length; i++) {
      const distance = haversineDistance(
        currentPoint[0],
        currentPoint[1],
        remaining[i].lat,
        remaining[i].lng
      );
      
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }
    
    // Add nearest point to sorted list
    const nearest = remaining[nearestIndex];
    sorted.push(nearest);
    
    // Update current point and remove from remaining
    currentPoint = [nearest.lat, nearest.lng];
    remaining.splice(nearestIndex, 1);
  }
  
  return sorted;
}

